// CopyLinkBox.jsx
import React, { useRef, useState, useEffect, memo } from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';

import useClickAway from '../hook/useClickAway.js';

import FadeInFromBottom from '../style/FadeInFromBottom.js';
import FadeOut from '../style/FadeOut.js';

import { ButtonId } from '../resource/mixpanel.js';

import CopyIcon from '../../img/ic-copy-link.svg';
import ResetButtonStyle from '../style/ResetButtonStyle.js';
import { ShareType } from '../hook/useShortShareLink.js';

export const CopyLinkBox = ({
  id,
  shareType,
  position,
  shortLink,
  isOnMobile,
  handleClose,
  copyTextWithToastr,
}) => {
  const [shouldFadeOut, setShouldFadeOut] = useState(false);
  const shouldTrackMessageId = !![
    ShareType.SHORT,
    ShareType.STORY,
    ShareType.POST,
  ].includes(shareType);

  const timerRef = useRef();
  const closeLinkBoxWithFadeOut = () => {
    setShouldFadeOut(true);
    timerRef.current = setTimeout(() => {
      handleClose();
    }, 600); // TODO: remote config
  };
  const copyButtonRef = useRef();
  useClickAway(copyButtonRef, closeLinkBoxWithFadeOut);

  useEffect(() => {
    return () => {
      clearTimeout(timerRef.current);
    };
  }, []);

  const handleCopyLink = async () => {
    try {
      await copyTextWithToastr({
        copiedText: shortLink,
        successfulText: 'story_copy_link',
        durationSec: 3,
        color: isOnMobile ? 'success' : 'info',
      });
      closeLinkBoxWithFadeOut();
      // eslint-disable-next-line no-empty
    } catch (error) {}
  };

  return (
    <StyledCopyLinkBox
      ref={copyButtonRef}
      shouldFadeOut={shouldFadeOut}
      position={position}
    >
      <LinkName>{shortLink}</LinkName>
      <CopyButton
        data-element_id={ButtonId.All.ButtonCopyLink}
        data-tracking_payload={{
          ...(shouldTrackMessageId && { messageId: id }),
        }}
        onClick={handleCopyLink}
      >
        <img src={CopyIcon} alt="copy" width={24} height={24} />
      </CopyButton>
    </StyledCopyLinkBox>
  );
};

CopyLinkBox.propTypes = {
  id: PropTypes.string,
  shareType: PropTypes.string,
  shortLink: PropTypes.string,
  position: PropTypes.oneOf(['right', 'left', 'center']),
  isOnMobile: PropTypes.bool,
  handleClose: PropTypes.func,
  copyTextWithToastr: PropTypes.func,
};

CopyLinkBox.defaultProps = {
  id: '',
  shareType: '',
  shortLink: '',
  position: 'right',
  isOnMobile: false,
  handleClose: () => null,
  copyTextWithToastr: () => null,
};

const StyledCopyLinkBox = styled.div`
  position: absolute;
  bottom: 48px;
  width: 260px;
  height: 40px;
  border-radius: 20px;
  border: solid 1px #646464;
  background-color: rgba(35, 35, 35, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  z-index: 1;
  ${({ position }) => {
    if (position === 'left') {
      return css`
        right: 0;
      `;
    } else if (position === 'center') {
      return css`
        left: -100px;
      `;
    } else {
      return css`
        left: 0;
      `;
    }
  }}

  animation: 0.6s forwards
    ${({ shouldFadeOut }) => (shouldFadeOut ? FadeOut : FadeInFromBottom)};
`;

const LinkName = styled.div`
  width: 188px;
  height: 14px;
  margin-left: 13px;
  font-size: 14px;
  line-height: 1;
  color: #fff;
  text-align: center;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const CopyButton = styled.button`
  ${ResetButtonStyle}
  width: 48px;
  height: 40px;
  margin: 0 0 0 8px;
  padding: 8px 12px;
  border-left: solid 1px #646464;
  background-color: #232323;

  @media (hover: hover) {
    :hover {
      background-color: #474747;
    }
  }
`;

export default memo(CopyLinkBox);
